/**
 * `.zip` ↔ editor image-anchor coordinate conversion.
 *
 * Legacy `.zip` image-marker scenes store top-level transforms in Unity
 * AR Foundation's anchor-local frame (+Y = image-normal, +Z = image-top,
 * LH). The editor uses an OpenXR-style anchor frame (+Z = image-normal,
 * +Y = image-top, RH). `loadCmsScene` applies this conversion on import
 * (after the `.zip` loader populates the scene root); `TraceExport`
 * applies it on export when the scene root carries `ImageAnchor`.
 *
 * The conversion bundles handedness and axis-relabel into a single
 * proper rotation: pre-multiply by M = Rπ about (Y+Z)/√2. Self-inverse
 * (180° about a fixed axis), so one function handles both directions.
 *
 * Other `.zip` content (Anywhere scenes) is NOT touched — UnityGLTF
 * writes those transforms in glTF-RH directly at zip-write time, matching
 * the editor's frame. Newer formats (`.glxf`, `.tracex`, `.trace`) are
 * OpenXR-native end-to-end and never get this conversion.
 *
 * `ImageMarkerLoader.applyAnchorRotation` then maps the editor anchor
 * frame to world via an orientation-only rotation:
 *
 *     Vertical:   scene-root identity.   Anchor +Z normal = world +Z.
 *     Horizontal: scene-root Rx(-π/2).   Anchor +Z normal → world +Y.
 *
 * Axis mapping (after conversion):
 *
 *     +X (image-right)       ↔ -X
 *     +Y (Unity normal)      ↔ +Z (editor normal)
 *     +Z (Unity image-top)   ↔ +Y (editor image-top)
 */

import { Matrix, Quaternion } from '@babylonjs/core/Maths/math.vector';

type Vec3 = [number, number, number];
type Quat = [number, number, number, number];

/**
 * Convert a position between Unity AR Foundation image-anchor LH and
 * editor RH. Self-inverse: `(x, y, z) → (-x, z, y)`.
 */
export function convertImageAnchorPosition(p: Vec3 | undefined): Vec3 | undefined {
    if (!p) return p;
    return [-p[0], p[2], p[1]];
}

/**
 * Convert a rotation between Unity AR Foundation image-anchor LH and
 * editor RH. Self-inverse: pre-multiply by M = Rπ about (Y+Z)/√2.
 */
export function convertImageAnchorRotation(q: Quat | undefined): Quat | undefined {
    if (!q) return q;
    const rotation = new Matrix();
    new Quaternion(q[0], q[1], q[2], q[3]).toRotationMatrix(rotation);
    // We want to pre-multiply by M (column-vector composition `M · R`,
    // i.e. apply R first then M). Babylon's `a.multiply(b)` stores
    // values such that the resulting matrix transforms a column vector
    // as `b · a · v` — so `rotation.multiply(M)` ≡ `M · R` on column
    // vectors. Verified empirically by the round-trip + orientation
    // unit tests.
    const composed = rotation.multiply(M);
    const out = Quaternion.FromRotationMatrix(composed);
    return [out.x, out.y, out.z, out.w];
}

// ── Implementation ─────────────────────────────────────────────────────

/**
 * `M = Rπ about (Y+Z)/√2` as a Babylon column-major matrix.
 *
 *     col 0 = M · e_x = (-1, 0, 0)
 *     col 1 = M · e_y = ( 0, 0, 1)
 *     col 2 = M · e_z = ( 0, 1, 0)
 */
const M: Matrix = Matrix.FromArray([
    -1, 0, 0, 0,
    0, 0, 1, 0,
    0, 1, 0, 0,
    0, 0, 0, 1,
]);

// ── Legacy aliases ──────────────────────────────────────────────────────
//
// Pre-rename names retained as deprecated aliases for one release cycle.
// New code should call `convertImageAnchor*` directly.

/** @deprecated Use {@link convertImageAnchorPosition}. */
export const convertZipPosition = convertImageAnchorPosition;
/** @deprecated Use {@link convertImageAnchorRotation}. */
export const convertZipRotation = convertImageAnchorRotation;
